import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import { formatDate, formatDateTime, formatNumber } from '../../../i18n/formatters';

export const API_BASE = (import.meta.env['VITE_API_BASE_URL'] ?? '') + '/api/v1';
export const PA_TOKEN_KEY = 'pa_token';

export const C = {
  text: 'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  border: 'var(--acc-color-border)',
  accent: 'var(--acc-color-primary)',
  surface: 'var(--acc-color-surface)',
  danger: 'var(--acc-color-danger)',
  warning: 'var(--acc-color-warning)',
  success: 'var(--acc-color-success)',
};

export class PaApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'PaApiError';
  }
}

export interface PaApiSuccess<T> {
  success: true;
  data: T;
}

function getToken(): string | null {
  return sessionStorage.getItem(PA_TOKEN_KEY);
}

export async function paFetch<T>(
  method: 'GET' | 'POST' | 'PATCH',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getToken();
  if (token) {
    headers['X-PA-Token'] = token;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  const data = await response.json();
  if (!data.success) {
    throw new PaApiError(response.status, data.error?.message ?? 'Unknown error');
  }

  return (data as PaApiSuccess<T>).data;
}

export function friendlyError(error: unknown): string {
  if (error instanceof PaApiError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong.';
}

export function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatMaybeDate(value: string | Date | null | undefined): string {
  return formatDate(value) || 'Not available';
}

export function formatMaybeDateTime(value: string | Date | null | undefined): string {
  return formatDateTime(value) || 'Not available';
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${formatNumber(amount)}`;
  }
}

export function toNullableNumber(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function toNullableIsoString(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function toDateTimeLocalInput(value: string | Date | null | undefined): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return offsetDate.toISOString().slice(0, 16);
}

export function asNullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function statusColor(status: string): string {
  switch (status) {
    case 'approved':
    case 'active':
    case 'customer':
    case 'paid':
      return C.success;
    case 'pending':
    case 'reviewing':
    case 'qualified':
    case 'trial':
    case 'draft':
      return C.warning;
    case 'rejected':
    case 'paused':
    case 'voided':
    case 'closed_lost':
    case 'cancelled':
    case 'terminated':
      return C.danger;
    default:
      return C.muted;
  }
}

export function Card({
  title,
  subtitle,
  action,
  children,
  style,
  className,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <section
      className={className}
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 20,
        padding: 22,
        boxShadow: 'var(--acc-shadow-sm)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 780, color: C.text }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginTop: 4 }}>
              {subtitle}
            </div>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled = false,
  type = 'button',
  title,
  style,
}: {
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  style?: CSSProperties;
}) {
  const variants: Record<string, CSSProperties> = {
    default: { background: '#fff', color: C.text, border: `1px solid ${C.border}` },
    primary: { background: C.accent, color: '#fff', border: `1px solid ${C.accent}` },
    danger: { background: C.danger, color: '#fff', border: `1px solid ${C.danger}` },
    ghost: { background: 'transparent', color: C.muted, border: `1px solid ${C.border}` },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        minHeight: 38,
        padding: '0 14px',
        borderRadius: 12,
        fontSize: 12.5,
        fontWeight: 760,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        ...variants[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function Pill({ label, tone }: { label: string; tone: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 760,
        border: `1px solid ${tone}2b`,
        background: `${tone}15`,
        color: tone,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: 7 }}>
      <span style={{ fontSize: 12, fontWeight: 760, color: C.text }}>{label}</span>
      {children}
    </label>
  );
}

export const inputStyle: CSSProperties = {
  width: '100%',
  minHeight: 42,
  padding: '10px 12px',
  borderRadius: 12,
  border: `1px solid ${C.border}`,
  background: '#fff',
  color: C.text,
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

export const textareaStyle: CSSProperties = {
  ...inputStyle,
  minHeight: 96,
  resize: 'vertical',
};

export function TableWrap({ children }: { children: ReactNode }) {
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 16 }}>
      {children}
    </div>
  );
}

export const thS: CSSProperties = {
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 760,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  background: '#fafafa',
  borderBottom: `1px solid ${C.border}`,
  textAlign: 'left',
};

export const tdS: CSSProperties = {
  padding: '11px 12px',
  borderBottom: '1px solid #f1f5f9',
  fontSize: 13,
  color: C.text,
  verticalAlign: 'top',
};
